// Shared signal-forms submission helpers (ADR 0012).
//
// Every signal `form()` routes submission through `submit()` and configures
// its `submission` options. These two helpers remove the per-screen
// boilerplate that ADR 0012 standardises:
//
//   - `focusFirstInvalid` — the single `submission.onInvalid` handler.
//   - `toServerErrors`     — the `kind: 'server'` + `fieldTree:` contract,
//                            encoded once, for self-persisting forms only.
//
// Verified against @angular/forms 22.0.1:
//   - `FieldState.errorSummary(): ValidationError.WithFieldTree[]`
//   - `ReadonlyFieldState.focusBoundControl()`
//   - `FormSubmitOptions.onInvalid?: (field: FieldTree<…>) => void`
//   - `FormSubmitOptions.action: (…) => Promise<TreeValidationResult>` where
//     `TreeValidationResult = … | OneOrMany<ValidationError.WithOptionalFieldTree>`

import type {
  FieldTree,
  ReadonlyFieldTree,
  ValidationError,
} from '@angular/forms/signals';

/**
 * `submission.onInvalid` handler: focuses the first invalid field after a
 * failed submit so the user lands on the problem instead of a silent no-op.
 *
 * Pass it directly — one symbol per form, no repeated errorSummary chain:
 *
 * ```ts
 * form(model, schema, {
 *   submission: { action, onInvalid: focusFirstInvalid },
 * });
 * ```
 */
export function focusFirstInvalid<TModel>(field: FieldTree<TModel>): void {
  field().errorSummary()[0]?.fieldTree().focusBoundControl();
}

/**
 * A single field-targeted server error. `fieldTree` is the sub-field path
 * itself (e.g. `form.contact_email`), not its called state.
 */
export interface ServerFieldError {
  readonly fieldTree: ReadonlyFieldTree<unknown>;
  readonly message: string;
}

/**
 * Maps resolved server-side field errors into the signal-forms submission-error
 * shape `[{ fieldTree, kind: 'server', message }]`. Return the result from a
 * self-persisting form's `submission.action` (inside its `catch`) so `submit()`
 * routes each message onto its field:
 *
 * ```ts
 * action: async (f) => {
 *   try {
 *     await firstValueFrom(api.save(f().value()));
 *     return;
 *   } catch (err) {
 *     return toServerErrors([
 *       { fieldTree: f.email, message: messageFor(err) },
 *     ]);
 *   }
 * },
 * ```
 *
 * Self-persisting forms ONLY — an emit-form's action returns before the
 * parent's HTTP resolves, so its server errors stay parent-side (ADR 0012).
 * This is the one place the `kind: 'server'` literal + `fieldTree:` binding
 * lives; callers resolve which field gets which message from their own error.
 */
export function toServerErrors(
  errors: readonly ServerFieldError[],
): ValidationError.WithFieldTree[] {
  return errors.map(({ fieldTree, message }) => ({
    fieldTree,
    kind: 'server',
    message,
  }));
}

/**
 * The narrowed server-error shape these forms read: a field-scoped
 * `{ error: { field, message } }`, with the top-level `message`/`status` as
 * fallbacks. Typed structurally so the helper needs no HTTP dependency.
 */
export interface ServerErrorResponse {
  readonly error?: { readonly field?: string; readonly message?: string };
  readonly message?: string;
  readonly status?: number;
}

/**
 * An extra routing rule for cases a field name can't express — e.g. a bare
 * `status === 409` conflict. The first rule whose `when` matches routes the
 * message onto its `fieldTree`.
 */
export interface ServerErrorRule {
  readonly when: (error: ServerErrorResponse) => boolean;
  readonly fieldTree: ReadonlyFieldTree<unknown>;
}

/**
 * Routes a server `error.field` onto its form field for a self-persisting
 * form's `submission.action` catch. Reads the server field name + message off
 * `err`, looks the field up in `fieldMap`, and returns the `toServerErrors`
 * array; `extraRules` cover non-field cases (e.g. a 409 conflict). Returns
 * `undefined` when nothing matches, so the global interceptor's toast stays
 * the surface.
 */
export function routeServerError(
  err: unknown,
  fieldMap: Record<string, ReadonlyFieldTree<unknown>>,
  extraRules: readonly ServerErrorRule[] = [],
): ValidationError.WithFieldTree[] | undefined {
  const res = err as ServerErrorResponse;
  const field = res?.error?.field;
  const message = res?.error?.message ?? res?.message;
  if (!message) return undefined;
  const mapped = field ? fieldMap[field] : undefined;
  if (mapped) return toServerErrors([{ fieldTree: mapped, message }]);
  for (const rule of extraRules) {
    if (rule.when(res)) {
      return toServerErrors([{ fieldTree: rule.fieldTree, message }]);
    }
  }
  return undefined;
}
